/**
 * Double-click policy for the EDG/FSM papers.
 *
 * React Flow binds d3-zoom's dblclick.zoom to `.react-flow__renderer` — the
 * parent of both the pane and the edges — and defaults zoomOnDoubleClick to
 * true. Rappid never zoomed on double-click (wheel + toolbar only).
 *
 * A transition double-click must add a knikpunt. The first click of that
 * gesture selects the transition and opens the inspector (a 300px column).
 * When EDG or OET is also open the FSM pane shrinks enough that the second
 * click often misses the 15px hit path and lands on the pane; without
 * recovery that zooms instead of inserting.
 *
 * Even when both clicks hit, the panes reflow between them, so the dblclick's
 * screen position no longer maps to the flow point the user aimed at. The
 * knikpunt is therefore anchored to the FIRST click (its transition and its
 * flow point, captured before any reflow); the second click is a follow-up
 * that must not toggle the selection (and the inspector) back off.
 */
import type { Pt } from './edgeGeometry';

/** Override React Flow's default (`true`). Wheel and the zoom toolbar still zoom. */
export const ZOOM_ON_DOUBLE_CLICK = false;

/** macOS/Chrome double-click window is typically ≤500ms. */
export const EDGE_DBLCLICK_MS = 500;

export type PendingEdgeClick = {
  edgeId: string;
  /** Flow point of the first click, captured before the panes reflow. */
  point: Pt;
  at: number;
};

export function rememberEdgeClick(edgeId: string, point: Pt, now = Date.now()): PendingEdgeClick {
  return { edgeId, point: { x: point.x, y: point.y }, at: now };
}

function freshPending(pending: PendingEdgeClick | null, now: number): PendingEdgeClick | null {
  if (pending == null || now - pending.at > EDGE_DBLCLICK_MS) return null;
  return pending;
}

/** The second click of a double-click that started on an edge. */
export function isDoubleClickFollowUp(input: {
  pending: PendingEdgeClick | null;
  detail: number;
  now?: number;
}): boolean {
  return input.detail >= 2 && freshPending(input.pending, input.now ?? Date.now()) != null;
}

/**
 * Where a dblclick on an edge inserts its knikpunt: at the first click when it
 * is still fresh (the reflow may have slid another edge, or another part of
 * this one, under the cursor), otherwise at the dblclick itself.
 */
export function resolveDoubleClickInsert(input: {
  pending: PendingEdgeClick | null;
  edgeId: string;
  point: Pt;
  now?: number;
}): { edgeId: string; point: Pt } {
  const pending = freshPending(input.pending, input.now ?? Date.now());
  if (pending) return { edgeId: pending.edgeId, point: pending.point };
  return { edgeId: input.edgeId, point: input.point };
}

/** Keep a transition double-click off the renderer zoom handler. */
export function consumeDiagramDoubleClick(evt: {
  stopPropagation(): void;
  preventDefault(): void;
}): void {
  evt.stopPropagation();
  evt.preventDefault();
}

/**
 * Recover a knikpunt insert when the second click of a double-click missed
 * the transition and hit the pane (inspector-open layout shift).
 */
export function recoverVertexInsertFromPaneClick(input: {
  pending: PendingEdgeClick | null;
  detail: number;
  now?: number;
}): PendingEdgeClick | null {
  if (input.detail < 2) return null;
  return freshPending(input.pending, input.now ?? Date.now());
}
